import { EngagementTile } from '@/components/contacts/EngagementTile'
import { Alert } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import type { MemberPortalOrganization } from './MemberSignIn'
import {
  portalDashboardKeys,
  portalDashboardTile,
  type PortalDashboardKey,
  type PortalDashboardResponse,
} from './portalDashboard.types'
import { MemberPortalGroups } from './MemberPortalGroups'
import { MemberPortalPeople } from './MemberPortalPeople'
import { MemberPortalSpend } from './MemberPortalSpend'
import { MemberPortalActivityList } from './MemberPortalActivityList'
import { MemberPortalEventRegistrations } from './MemberPortalEventRegistrations'

export interface DashboardSelection {
  selected?: PortalDashboardKey
  canManage: boolean
  // The portal opens the People list from the People tile; the staff preview keeps it a figure.
  people?: boolean
  tab: string
  selectTile: (tile: PortalDashboardKey) => void
}
export function PortalDashboardBody({
  dashboard,
  selection,
  organization,
  viewer,
}: {
  dashboard: PortalDashboardResponse['dashboard']
  selection: DashboardSelection
  organization: MemberPortalOrganization
  viewer: string
}) {
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-3">
          <span className="text-body font-medium text-foreground">Dashboard</span>
          <span className="text-caption text-muted-foreground">{dashboard.window.label}</span>
        </div>
        {dashboard.lapsed_banner && (
          <Alert variant="warning" className="rounded-none border-x-0 border-t-0">
            {dashboard.lapsed_banner}
          </Alert>
        )}
        {[portalDashboardKeys.slice(0, 3), portalDashboardKeys.slice(3)].map((keys, row) => (
          <div
            key={row}
            className={`grid grid-cols-1 items-stretch divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0 ${row ? 'border-t border-border' : ''}`}
          >
            {keys.map((key) => {
              const found = dashboard.tiles.find((item) => item.key === key)
              if (!found) return null
              const tile = portalDashboardTile(found, dashboard.people_count)
              return (
                <EngagementTile
                  key={key}
                  tile={{ ...tile, secondary_lines: [] }}
                  selected={selection.selected === key}
                  showComparison={row === 0}
                  onSelect={
                    key === 'roster' && !selection.people
                      ? undefined
                      : () => selection.selectTile(key)
                  }
                />
              )
            })}
          </div>
        ))}
      </Card>
      <DashboardList selection={selection} organization={organization} viewer={viewer} />
    </div>
  )
}

function DashboardList({
  selection,
  organization,
  viewer,
}: {
  selection: DashboardSelection
  organization: MemberPortalOrganization
  viewer: string
}) {
  if (!['dashboard', 'engagement'].includes(selection.tab)) return null
  return (
    <>
      {' '}
      {selection.selected === 'groups' && (
        <MemberPortalGroups
          key={`${viewer}:${organization.id}:groups`}
          organization={organization}
          viewer={viewer}
        />
      )}
      {selection.selected === 'roster' && selection.people && (
        <MemberPortalPeople
          key={`${viewer}:${organization.id}:people`}
          organizationId={organization.id}
          viewer={viewer}
        />
      )}
      {selection.selected === 'spend' && (
        <MemberPortalSpend
          key={`${viewer}:${organization.id}`}
          organization={organization}
          viewer={viewer}
        />
      )}
      {selection.selected === 'event_registrations' && (
        <MemberPortalEventRegistrations
          key={`${viewer}:${organization.id}`}
          organizationId={organization.id}
          viewer={viewer}
        />
      )}
      {['courses_completed', 'personal_memberships'].includes(selection.selected ?? '') && (
        <MemberPortalActivityList
          key={`${viewer}:${organization.id}:${selection.selected}`}
          organizationId={organization.id}
          viewer={viewer}
          kind={selection.selected === 'courses_completed' ? 'courses' : 'personal_memberships'}
        />
      )}
    </>
  )
}
